import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  MessageSquare, Send, X, Loader2, Bot, User, Sparkles,
  CheckCircle2, AlertCircle, Wrench, ChevronDown, ChevronUp,
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import logger from '../../lib/logger';
import './ClassificationAgentChat.css';

/**
 * ClassificationAgentChat — embedded Claude agent for flag review dialogue.
 *
 * Provides a chat interface that connects to the backend classification agent.
 * The agent can:
 *   - Research products via GPL catalog and web search
 *   - Create new tech_components
 *   - Add product patterns for deterministic matching
 *   - Reclassify items with proper wave-id audit trail
 *   - Resolve flags
 *
 * All actions follow CLASSIFICATION_NORTH_STAR.md protocols.
 */
// Poll the session row every POLL_INTERVAL_MS while the agent loop is
// running. Agent loops can run 60–160s, far longer than apiClient's 45s
// per-request timeout, so we kick off the loop in the background on the
// server and stream progress by polling instead of holding the request open.
const POLL_INTERVAL_MS = 2000;
const POLL_MAX_MS = 5 * 60 * 1000; // 5 min hard cap; loops finish under this

// Normalize a backend message (Anthropic content blocks) into the flat
// {role, content, timestamp} shape the UI renders.
function normalizeServerMessage(msg) {
  if (!msg) return null;
  if (typeof msg.content === 'string') {
    return { role: msg.role, content: msg.content, timestamp: new Date() };
  }
  if (Array.isArray(msg.content)) {
    // Tool-result user blocks: hide from chat scroll (they're noise to the operator).
    const isToolResult = msg.role === 'user' && msg.content.every(b => b?.type === 'tool_result');
    if (isToolResult) return null;
    const text = msg.content
      .filter(b => b?.type === 'text')
      .map(b => b.text)
      .join('\n')
      .trim();
    if (!text && msg.role === 'assistant') {
      // Assistant turn with only tool_use blocks — render a compact "working"
      // marker so the operator sees forward progress mid-loop.
      const toolNames = msg.content.filter(b => b?.type === 'tool_use').map(b => b.name);
      if (toolNames.length) {
        return { role: 'assistant', content: `_Using tools: ${toolNames.join(', ')}_`, timestamp: new Date(), _tool_only: true };
      }
      return null;
    }
    if (!text) return null;
    return { role: msg.role, content: text, timestamp: new Date() };
  }
  return null;
}

function ClassificationAgentChat({ flag, onClose, onResolved }) {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [usage, setUsage] = useState({ total_cost_usd: 0, total_input_tokens: 0, total_output_tokens: 0 });
  const [statusMessage, setStatusMessage] = useState(null);
  const [actionsExpanded, setActionsExpanded] = useState(false);
  const [actions, setActions] = useState([]);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const pollTimerRef = useRef(null);
  const pollStartedAtRef = useRef(null);
  const resolveNotifiedRef = useRef(false);
  const prevMessageCountRef = useRef(0);

  // Auto-scroll the inner messages container only — NOT scrollIntoView,
  // which walks every ancestor and yanks the whole page down when the
  // poller refreshes mid-loop. Also: only scroll if the user is already
  // near the bottom, so manually scrolling up to re-read doesn't get
  // hijacked on the next 2s poll tick.
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const grew = messages.length > prevMessageCountRef.current;
    prevMessageCountRef.current = messages.length;
    if (!grew) return;
    const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
    if (distanceFromBottom < 120) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    inputRef.current?.focus();
  }, [sessionId]);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    pollStartedAtRef.current = null;
  }, []);

  // Clean up timer on unmount so leaving the flag mid-run doesn't leak polls.
  useEffect(() => () => stopPolling(), [stopPolling]);

  // Poll session state every POLL_INTERVAL_MS until chat_running flips false
  // or chat_error appears. Re-applies the full server message history each
  // tick so mid-loop turns show up incrementally.
  const pollOnce = useCallback(async (sid) => {
    try {
      const sessionRes = await apiClient.get(`/api/classification/agent/session/${sid}`);
      if (!sessionRes.ok) throw new Error(sessionRes.error || 'session fetch failed');

      const s = sessionRes.session || {};
      const serverMessages = (s.messages || [])
        .map(normalizeServerMessage)
        .filter(Boolean);
      setMessages(serverMessages);
      setUsage({
        total_cost_usd: Number(s.total_cost_usd) || 0,
        total_input_tokens: s.total_input_tokens || 0,
        total_output_tokens: s.total_output_tokens || 0,
      });
      setStatusMessage(s.chat_status_message || null);

      if (sessionRes.actions?.length > 0) {
        setActions(sessionRes.actions);
        if (!resolveNotifiedRef.current) {
          const resolveAction = sessionRes.actions.find(a => a.action_type === 'resolve_flag');
          if (resolveAction) {
            resolveNotifiedRef.current = true;
            onResolved?.();
          }
        }
      }

      if (s.chat_error) {
        setError(s.chat_error);
        setStatusMessage(null);
        setIsLoading(false);
        stopPolling();
        return;
      }

      if (!s.chat_running) {
        setStatusMessage(null);
        setIsLoading(false);
        stopPolling();
        return;
      }

      // Cap runaway polls (server-side loop is bounded to 20 turns, but be safe).
      if (pollStartedAtRef.current && Date.now() - pollStartedAtRef.current > POLL_MAX_MS) {
        setError(`Agent run exceeded ${POLL_MAX_MS / 1000}s — stopping poll. Check server logs.`);
        setIsLoading(false);
        stopPolling();
        return;
      }

      pollTimerRef.current = setTimeout(() => pollOnce(sid), POLL_INTERVAL_MS);
    } catch (e) {
      logger.ui.warn('AgentChat', 'poll_error', { error: e.message });
      // Transient network errors shouldn't kill the run — retry next tick.
      pollTimerRef.current = setTimeout(() => pollOnce(sid), POLL_INTERVAL_MS);
    }
  }, [onResolved, stopPolling]);

  const startPolling = useCallback((sid) => {
    stopPolling();
    pollStartedAtRef.current = Date.now();
    resolveNotifiedRef.current = false;
    pollOnce(sid);
  }, [pollOnce, stopPolling]);

  // Create session and send initial context
  const initSession = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Build initial context message with flag details
      const hintText = flag.hint_texts?.join('\n') || flag.latest_hint || '';
      const initialMessage = `I'm reviewing a flagged item that needs your help.

**Item Details:**
- ID: ${flag.item_id}
- Supplier: ${flag.supplier_name || 'Unknown'}
- Amount: $${Number(flag.line_total || 0).toLocaleString()}
- Description: "${flag.item_description || ''}"

**Current Classification:**
- Component: ${flag.current_component_name || 'unclassified'}
- Vendor: ${flag.current_vendor_name || 'unknown'}
- Method: ${flag.classification_method || 'unknown'}
- Confidence: ${flag.component_confidence ? `${(flag.component_confidence * 100).toFixed(0)}%` : 'N/A'}

**Operator Feedback:**
${hintText || 'No specific hint provided.'}

**Flag Categories:** ${(flag.categories || []).join(', ') || 'other'}

Please investigate this classification and fix it if needed. You can:
1. Research the product (GPL catalog, web search)
2. Create a new component if needed
3. Add patterns to prevent future misclassifications
4. Reclassify the item
5. Resolve the flag`;

      const res = await apiClient.post('/api/classification/agent/session', {
        context_type: 'flag_review',
        item_ids: [flag.item_id],
        flag_ids: flag.flag_ids || [flag.flag_id],
        initial_message: initialMessage,
      });

      if (!res.ok) throw new Error(res.error || 'Failed to create session');

      const sid = res.session.session_id;
      setSessionId(sid);
      // Show the operator's framing message immediately — polling will pick up
      // the same message from the server within 2s and reconcile.
      setMessages([{ role: 'user', content: initialMessage, timestamp: new Date() }]);
      logger.ui.info('AgentChat', 'session_created', { session_id: sid, item_id: flag.item_id });

      if (res.chat_running) {
        startPolling(sid);
      } else {
        setIsLoading(false);
      }
    } catch (e) {
      logger.ui.error('AgentChat', 'init_error', { error: e.message });
      setError(e.message);
      setIsLoading(false);
    }
  }, [flag, startPolling]);

  // Initialize on mount
  useEffect(() => {
    initSession();
  }, [initSession]);

  // Send a message
  const sendMessage = async () => {
    if (!input.trim() || !sessionId || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(m => [...m, { role: 'user', content: userMessage, timestamp: new Date() }]);
    setIsLoading(true);
    setError(null);

    try {
      const res = await apiClient.post(`/api/classification/agent/session/${sessionId}/chat`, {
        message: userMessage,
      });

      if (!res.ok) throw new Error(res.error || 'Failed to send message');

      logger.ui.info('AgentChat', 'message_sent', { session_id: sessionId });
      startPolling(sessionId);
    } catch (e) {
      logger.ui.error('AgentChat', 'send_error', { error: e.message });
      setError(e.message);
      setMessages(m => [...m, {
        role: 'error',
        content: e.message,
        timestamp: new Date(),
      }]);
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const completeSession = async () => {
    if (!sessionId) return;
    try {
      await apiClient.post(`/api/classification/agent/session/${sessionId}/complete`);
      logger.ui.info('AgentChat', 'session_completed', { session_id: sessionId });
    } catch (e) {
      logger.ui.warn('AgentChat', 'complete_error', { error: e.message });
    }
    onClose();
  };

  return (
    <div className="agent-chat">
      <header className="agent-chat__header">
        <div className="agent-chat__title">
          <Bot size={18} />
          <span>Classification Agent</span>
          <span className="agent-chat__badge">Claude</span>
        </div>
        <div className="agent-chat__meta">
          <span className="agent-chat__cost" title="Session cost">
            ${usage.total_cost_usd?.toFixed(4) || '0.0000'}
          </span>
          <button
            className="agent-chat__close"
            onClick={completeSession}
            title="Close chat"
          >
            <X size={16} />
          </button>
        </div>
      </header>

      {actions.length > 0 && (
        <div className="agent-chat__actions">
          <button
            className="agent-chat__actions-toggle"
            onClick={() => setActionsExpanded(e => !e)}
          >
            <Wrench size={12} />
            {actions.length} action{actions.length !== 1 ? 's' : ''} taken
            {actionsExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
          {actionsExpanded && (
            <ul className="agent-chat__actions-list">
              {actions.map((a, i) => (
                <li key={i} className="agent-chat__action-item">
                  <CheckCircle2 size={10} />
                  <span className="agent-chat__action-type">{a.action_type.replace(/_/g, ' ')}</span>
                  <span className="agent-chat__action-wave">{a.wave_id}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="agent-chat__messages" ref={messagesContainerRef}>
        {messages.map((msg, i) => (
          <div
            key={i}
            className={`agent-chat__message agent-chat__message--${msg.role}`}
          >
            <div className="agent-chat__message-icon">
              {msg.role === 'user' ? <User size={14} /> : msg.role === 'error' ? <AlertCircle size={14} /> : <Bot size={14} />}
            </div>
            <div className="agent-chat__message-content">
              <MessageContent content={msg.content} />
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="agent-chat__message agent-chat__message--assistant agent-chat__message--loading">
            <div className="agent-chat__message-icon">
              <Bot size={14} />
            </div>
            <div className="agent-chat__message-content">
              <Loader2 size={14} className="agent-chat__spin" />
              <span>{statusMessage || 'Thinking…'}</span>
            </div>
          </div>
        )}
        {error && !isLoading && (
          <div className="agent-chat__error">
            <AlertCircle size={14} />
            {error}
          </div>
        )}
      </div>

      <footer className="agent-chat__input-area">
        <textarea
          ref={inputRef}
          className="agent-chat__input"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask the agent to investigate, create components, add patterns..."
          rows={2}
          disabled={isLoading || !sessionId}
        />
        <button
          className="agent-chat__send"
          onClick={sendMessage}
          disabled={!input.trim() || isLoading || !sessionId}
          title="Send message (Enter)"
        >
          {isLoading ? <Loader2 size={16} className="agent-chat__spin" /> : <Send size={16} />}
        </button>
      </footer>

      <div className="agent-chat__footer">
        <span className="agent-chat__footer-hint">
          <Sparkles size={10} /> Agent can create components, add patterns, reclassify items, and resolve flags
        </span>
      </div>
    </div>
  );
}

/**
 * Render message content with basic markdown-like formatting
 */
function MessageContent({ content }) {
  if (!content) return null;

  return (
    <div className="agent-chat__formatted">
      {content.split('\n').map((line, i) => {
        // Headers
        if (line.startsWith('## ')) {
          return <h4 key={i} className="agent-chat__h2">{line.slice(3)}</h4>;
        }
        if (line.startsWith('# ')) {
          return <h3 key={i} className="agent-chat__h1">{line.slice(2)}</h3>;
        }
        // Bold
        if (line.includes('**')) {
          const parts = line.split(/\*\*(.+?)\*\*/g);
          return (
            <p key={i}>
              {parts.map((part, j) => j % 2 === 1 ? <strong key={j}>{part}</strong> : part)}
            </p>
          );
        }
        // List items
        if (line.match(/^[-*]\s/)) {
          return <li key={i}>{line.slice(2)}</li>;
        }
        if (line.match(/^\d+\.\s/)) {
          return <li key={i}>{line.slice(line.indexOf('.') + 2)}</li>;
        }
        // Code inline
        if (line.includes('`')) {
          const parts = line.split(/`(.+?)`/g);
          return (
            <p key={i}>
              {parts.map((part, j) => j % 2 === 1 ? <code key={j}>{part}</code> : part)}
            </p>
          );
        }
        // Regular paragraph
        if (line.trim()) {
          return <p key={i}>{line}</p>;
        }
        return null;
      })}
    </div>
  );
}

export default ClassificationAgentChat;
